import React, { useMemo, useState, type ReactNode, type PropsWithChildren } from 'react';
import {
  DndContext,
  DragOverlay,
  defaultDropAnimationSideEffects,
  KeyboardSensor,
  PointerSensor,
  type DropAnimation,
  useSensor,
  useSensors,
  type Active,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { DraggableListItem, DragHandle } from './DraggableListItem';

interface BaseItem {
  id: UniqueIdentifier;
  [key: string]: unknown;
}

interface DraggableListProps<T extends BaseItem> {
  items: T[];
  onChange?: (items: T[]) => void;
  renderItem: (item: T, isActive?: boolean, options?: {
    isOver?: boolean;
    overIndex?: number;
    itemIndex?: number;
  }) => ReactNode;
  activationConstraint?: { distance: number };
  showDropLine?: boolean;
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

function SortableDragOverlay({ children }: PropsWithChildren<{}>) {
  return <DragOverlay dropAnimation={dropAnimationConfig}>{children}</DragOverlay>;
}

export function DraggableList<T extends BaseItem>({
  items,
  onChange,
  renderItem,
  activationConstraint,
}: DraggableListProps<T>) {
  const [active, setActive] = useState<Active | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | undefined>(undefined);
  const activeItem = useMemo(() => items.find((item) => item.id === active?.id), [active, items]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: activationConstraint ? activationConstraint : undefined
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = (newItems: T[]) => {
    if (onChange) onChange(newItems);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActive(event.active);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          const oldIndex = items.findIndex((i) => i.id === active.id);
          const newIndex = items.findIndex((i) => i.id === over.id);
          handleChange(arrayMove(items, oldIndex, newIndex));
        }
        setActive(null);
        setOverId(undefined);
      }}
      onDragCancel={() => {
        setActive(null);
        setOverId(undefined);
      }}
    >
      <SortableContext items={items}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item, index) => {
            const isOver = overId === item.id;
            const overIndex = items.findIndex(i => i.id === overId);

            return (
              <React.Fragment key={item.id}>
                {renderItem(item, false, {
                  isOver,
                  overIndex: isOver ? overIndex : undefined,
                  itemIndex: index
                })}
              </React.Fragment>
            );
          })}
        </ul>
      </SortableContext>
      <SortableDragOverlay>
        {activeItem ? renderItem(activeItem, true) : null}
      </SortableDragOverlay>
    </DndContext>
  );
}

DraggableList.Item = DraggableListItem;
DraggableList.DragHandle = DragHandle;
